/**
 * Messaging API — conversations, messages, unread counts.
 * Text-only DM + Support chat. Uses RPC for list/count to avoid N+1.
 */

import { supabase } from '@turnifly/shared/api'

// ── TYPES ─────────────────────────────────────────────────

export interface ConversationListItem {
  id: string
  type: 'direct' | 'support'
  participant: {
    id: string
    full_name: string
    avatar_url: string | null
    badge_type: string | null
  } | null
  lastMessage: {
    text: string | null
    created_at: string
    sender_id: string
    media_urls: string[]
  } | null
  unreadCount: number
}

export interface MessageItem {
  id: string
  sender_id: string
  text: string | null
  media_urls: string[]
  is_read: boolean
  created_at: string
  edited_at: string | null
}

// ── CONVERSATIONS (via RPC — single query) ────────────────

interface ConversationRpcRow {
  conversation_id: string
  conversation_type: string
  other_user_id: string | null
  other_user_name: string | null
  other_user_avatar: string | null
  other_user_badge: string | null
  last_message_text: string | null
  last_message_sender_id: string | null
  last_message_at: string | null
  last_message_media_urls: string[] | null
  unread_count: number
}

export async function fetchConversations(userId: string): Promise<ConversationListItem[]> {
  const { data } = await supabase
    .rpc('get_conversations_list', { p_user_id: userId })
    .throwOnError()

  if (!data) return []

  return (data as ConversationRpcRow[]).map((row): ConversationListItem => ({
    id: row.conversation_id,
    type: row.conversation_type as 'direct' | 'support',
    participant: row.other_user_id
      ? {
          id: row.other_user_id,
          full_name: row.other_user_name ?? '',
          avatar_url: row.other_user_avatar,
          badge_type: row.other_user_badge,
        }
      : null,
    lastMessage: (row.last_message_text || row.last_message_media_urls?.length)
      ? {
          text: row.last_message_text,
          created_at: row.last_message_at ?? '',
          sender_id: row.last_message_sender_id ?? '',
          media_urls: Array.isArray(row.last_message_media_urls) ? row.last_message_media_urls : [],
        }
      : null,
    unreadCount: row.unread_count,
  }))
}

// ── TOTAL UNREAD (via RPC — lightweight) ──────────────────

export async function fetchTotalUnreadCount(userId: string): Promise<number> {
  const { data } = await supabase
    .rpc('get_unread_count', { p_user_id: userId })
    .throwOnError()

  return (data as number) ?? 0
}

// ── MESSAGES ────────────────────────────────────────────────

export async function fetchMessages(
  conversationId: string,
  limit = 50,
  before?: string,
): Promise<MessageItem[]> {
  let query = supabase
    .from('messages')
    .select('id, sender_id, text, media_urls, is_read, created_at, edited_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (before) {
    query = query.lt('created_at', before)
  }

  const { data } = await query.throwOnError()
  return ((data ?? []) as MessageItem[]).reverse()
}

export async function sendMessage(
  conversationId: string,
  senderId: string,
  text: string | null,
  mediaUrls: string[] = [],
): Promise<MessageItem> {
  const payload: Record<string, unknown> = {
    conversation_id: conversationId,
    sender_id: senderId,
  }
  if (text && text.trim()) payload.text = text.trim()
  if (mediaUrls.length > 0) payload.media_urls = mediaUrls

  const { data } = await supabase
    .from('messages')
    .insert(payload)
    .select('id, sender_id, text, media_urls, is_read, created_at, edited_at')
    .single()
    .throwOnError()
  return data as MessageItem
}

export async function markConversationRead(
  conversationId: string,
  userId: string,
): Promise<void> {
  await supabase
    .from('conversation_participants')
    .update({ last_read_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('user_id', userId)
    .throwOnError()
}

// ── CREATE / FIND CONVERSATION ──────────────────────────────

interface ConversationRef {
  id: string
}

export async function findOrCreateDirectConversation(
  _userId: string,
  otherUserId: string,
): Promise<ConversationRef> {
  // Uses SECURITY DEFINER RPC — atomic find-or-create with
  // premium, follow, and block validation at DB level.
  const { data } = await supabase
    .rpc('find_or_create_direct_conversation', { p_other_user_id: otherUserId })
    .throwOnError()

  return { id: data as string }
}

export async function findOrCreateSupportConversation(
  _userId: string,
): Promise<ConversationRef> {
  // Uses SECURITY DEFINER RPC — atomic find-or-create.
  const { data } = await supabase
    .rpc('find_or_create_support_conversation')
    .throwOnError()

  return { id: data as string }
}

// ── EDIT MESSAGE ───────────────────────────────────────────

export async function updateMessageText(
  messageId: string,
  newText: string,
): Promise<void> {
  await supabase
    .from('messages')
    .update({ text: newText.trim(), edited_at: new Date().toISOString() })
    .eq('id', messageId)
    .throwOnError()
}
